<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import moment from "moment-timezone";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { ModalName } from '@/constants/modals';
import { formattedAddress } from "@/helpers/address";
import { clientService } from "@/modules/clients/services/clientService";
import { toKnownMissionStatus } from "@/modules/companyNeed/helpers/mission";
import { createTempId } from "@/modules/companyNeed/helpers/tempId";
import missionService from "@/modules/companyNeed/services/missionService";
import { useCompanyNeedStore } from "@/modules/companyNeed/store/companyNeedStore";
import { useMissionPatchStore } from "@/modules/companyNeed/store/missionPatchStore";
import { type MissionList, MissionStatus } from "@/modules/companyNeed/views/details/missions/types";
import { useConfigurationStore } from "@/store/configurationStore";
import { EContractType } from '@/types/contract';
import { type Remuneration, RemunerationPeriodicity } from "@/types/remuneration";

import type { DateRange } from "@/components/Mission/DateRangeTable/types";
import type { FetchCompanyAddressResponse } from "@/modules/clients/types";
import type {
    CreateMissionPayload,
    ManageOrderModalProps,
    OrderAddress,
    UpdateMissionPayload,
} from "@/modules/companyNeed/components/modals/createOrder/types";
import type { MissionRowPatch } from "@/modules/companyNeed/store/missionPatchStore";
import type { IAddress } from '@/types/address';
import type { LabelValue } from '@/types/common';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import ModalSection from "@/dsComponents/modals/ModalSection.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import Toggle from "@/dsComponents/Toggle/Toggle.vue";

import ContractsDropdown from "@/components/dropdown/ContractsDropdown.vue";
import HiringReasonsDropdown from "@/components/dropdown/HiringReasonsDropdown.vue";
import RemunerationDropdown from "@/components/dropdown/RemunerationDropdown.vue";
import { InputMode } from "@/components/dropdown/types";
import DateRangeTable from "@/components/Mission/DateRangeTable/DateRangeTable.vue";
import { useDateRange } from "@/components/Mission/DateRangeTable/useDateRange";
import MissionLocationModal from "@/components/Mission/MissionLocationModal.vue";

interface FormSnapshot {
    name: string;
    hiringReasonId: string | undefined;
    justification: string;
    addressJson: string;
    contractTypeValue: string;
    totalPositions: number;
    showRemuneration: boolean;
    remunerationJson: string;
    periodsJson: string;
    description: string;
    note: string;
}

interface SubmitErrorLike {
    response?: { data?: { message?: string } };
    fullErr?: { response?: { data?: { message?: string } } };
}

const DEFAULT_REMUNERATION: Remuneration = {
    periodicity: RemunerationPeriodicity.HOURLY,
    minimumAmount: 0,
    maximumAmount: 0,
};

const props = defineProps<ManageOrderModalProps>();

const emit = defineEmits<{
    close: [];
    success: [];
}>();

const { t } = useI18n();
const { toastSuccess, toastError } = useDefaultToast();
const modalStore = useModalStore();
const configStore = useConfigurationStore();
const companyNeedStore = useCompanyNeedStore();
const missionPatchStore = useMissionPatchStore();
const { dateRanges, validate } = useDateRange();

const orderName = ref("");
const address = ref<IAddress | undefined>();
const isLocationsModalOpen = ref(false);
const quantity = ref(1);
const description = ref("");
const note = ref("");
const showInternalNote = ref(false);
const showSalaryInProposal = ref(false);
const isLoading = ref(false);
const error = ref<Record<string, boolean>>({});
const companyAddresses = ref<FetchCompanyAddressResponse[]>([]);
const hiringReason = ref<LabelValue | null>(null);
const justification = ref("");
const initialSnapshot = ref<FormSnapshot | null>(null);
const contract = ref<LabelValue>({
    label: t(`mission.job.contractType.select.${EContractType.INTERIM}`),
    value: EContractType.INTERIM,
});
const propositionRemuneration = ref<Remuneration>(props.mission?.remuneration ?? props.besoinRemuneration ?? { ...DEFAULT_REMUNERATION });

const isEdit = computed(() => !!props.mission && !props.isDuplicate);
const isDuplicate = computed(() => !!props.mission && !!props.isDuplicate);

const modalTitle = computed(() => {
    if (isEdit.value) return t("page.order.editOrder");
    if (isDuplicate.value) return t("page.order.orderDuplication");
    return t("page.order.newOrder");
});

const missionLocations = computed<IAddress[]>(() => {
    const mapped = companyAddresses.value.map(
        (addr): IAddress => ({
            name: addr.name ?? "",
            street: addr.street ?? "",
            complement: addr.complement ?? "",
            city: addr.city ?? "",
            zip: addr.zip ?? "",
            countryCode: addr.countryCode ?? "",
            citySlug: "",
            location: addr.latitude != null && addr.longitude != null ? [addr.longitude, addr.latitude] : [],
        }),
    );
    if (mapped.length > 0) return mapped;
    return props.besoinAddress ? [props.besoinAddress] : [];
});

const hasRemuneration = computed(
    () =>
        (propositionRemuneration.value.minimumAmount && propositionRemuneration.value.minimumAmount > 0) ||
        (propositionRemuneration.value.maximumAmount && propositionRemuneration.value.maximumAmount > 0),
);

const hasDates = computed(() =>
    dateRanges.value.some((range) => range.startDate != null && range.endDate != null && Boolean(range.startTime) && Boolean(range.endTime)),
);

const isFormValid = computed(
    () => Boolean(address.value) && Boolean(contract.value.value) && Number.isFinite(quantity.value) && quantity.value > 0 && hasDates.value,
);

const effectiveMinDate = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
});

const getSubmitErrorMessage = (submitError: unknown): string => {
    const errorLike = submitError as SubmitErrorLike;
    return errorLike?.response?.data?.message ?? errorLike?.fullErr?.response?.data?.message ?? t("errors.Global.ServerError._default");
};

const takeSnapshot = (): FormSnapshot => ({
    name: orderName.value,
    hiringReasonId: hiringReason.value?.value,
    justification: justification.value,
    addressJson: JSON.stringify(address.value),
    contractTypeValue: String(contract.value.value).toUpperCase(),
    totalPositions: quantity.value,
    showRemuneration: showSalaryInProposal.value,
    remunerationJson: JSON.stringify(propositionRemuneration.value),
    periodsJson: JSON.stringify(dateRanges.value),
    description: description.value,
    note: showInternalNote.value ? note.value : "",
});

const toISOWithTime = (timestamp: number, time: string): string => {
    const parsedTime = moment(time, "Hmm");
    return moment(timestamp).hours(parsedTime.hours()).minutes(parsedTime.minutes()).seconds(0).milliseconds(0).toISOString();
};

const toEpochWithTime = (timestamp: number, time: string): number => {
    const parsedTime = moment(time, "Hmm");
    return moment(timestamp).hours(parsedTime.hours()).minutes(parsedTime.minutes()).seconds(0).milliseconds(0).valueOf();
};

const buildOptimisticPeriods = (): MissionList["periods"] =>
    dateRanges.value
        .filter((range) => range.startDate != null && range.endDate != null && range.startTime && range.endTime)
        .map((range) => ({
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            start: toEpochWithTime(range.startDate!, range.startTime!),
            // biome-ignore lint/style/noNonNullAssertion: Init Biome
            end: toEpochWithTime(range.endDate!, range.endTime!),
        }));

// Unnamed orders get "MIS-{count + 1}" from the backend, counting every mission of the
// company need, canceled ones included: only the unfiltered missions list gives that count.
const companyNeedMissionIds = ref<Set<string> | null>(null);

const loadCompanyNeedMissionIds = async () => {
    try {
        const missions = await missionService.fetchMissions(props.besoinId);
        companyNeedMissionIds.value = new Set(missions.map((mission) => mission.id));
    } catch {
        // The optimistic row falls back to the generic name
    }
};

const getOptimisticMissionName = (isDisplayedCompanyNeed: boolean): string => {
    if (orderName.value) return orderName.value;
    const missionIds = companyNeedMissionIds.value;
    if (!missionIds) return t("page.order.newOrder");
    // Missions created optimistically that the query-side list does not return yet
    const pendingCount = isDisplayedCompanyNeed
        ? [...companyNeedStore.optimisticMissionIds].filter((id) => !missionIds.has(id)).length
        : 0;
    return `MIS-${missionIds.size + pendingCount + 1}`;
};

const buildOptimisticMission = (selectedAddress: IAddress | undefined, isDisplayedCompanyNeed: boolean): MissionList => {
    return {
        id: createTempId(),
        status: MissionStatus.TO_PROCESS,
        name: getOptimisticMissionName(isDisplayedCompanyNeed),
        totalPositions: quantity.value,
        periods: buildOptimisticPeriods(),
        city: selectedAddress?.city ?? "",
        contractType: String(contract.value.value).toUpperCase(),
        states: [],
        availableActions: [],
        confirmedWorkers: 0,
        workers: [],
        workersSelection: [],
    };
};

const mapDateRangeToPeriod = ({ startDate, startTime, endDate, endTime, weekendIncluded }: DateRange) => ({
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    start: toISOWithTime(startDate!, startTime!),
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    end: toISOWithTime(endDate!, endTime!),
    weekendIncluded,
});

const mapAddressToOrderAddress = (selectedAddress: IAddress): OrderAddress => ({
    city: selectedAddress.city,
    countryCode: selectedAddress.countryCode ?? "",
    citySlug: selectedAddress.citySlug,
    name: selectedAddress.name,
    street: selectedAddress.street,
    complement: selectedAddress.complement,
    zip: selectedAddress.zip,
    location: selectedAddress.location ?? [],
});

const buildCreatePayload = (selectedAddress: IAddress | undefined): CreateMissionPayload => ({
    besoinId: props.besoinId,
    companyId: props.companyId,
    jobId: props.jobId,
    name: orderName.value,
    hiringReason: hiringReason.value?.value,
    justification: justification.value || undefined,
    address: selectedAddress ? mapAddressToOrderAddress(selectedAddress) : { city: "", countryCode: "", location: [] },
    contractType: String(contract.value.value).toUpperCase(),
    totalPositions: quantity.value,
    showRemuneration: showSalaryInProposal.value,
    remunerationPeriodicity: hasRemuneration.value ? propositionRemuneration.value.periodicity ?? undefined : undefined,
    minRemuneration: hasRemuneration.value ? propositionRemuneration.value.minimumAmount ?? undefined : undefined,
    maxRemuneration: hasRemuneration.value ? propositionRemuneration.value.maximumAmount ?? undefined : undefined,
    periods: dateRanges.value.map(mapDateRangeToPeriod),
    description: description.value || undefined,
    note: showInternalNote.value ? note.value : undefined,
});

const buildUpdatePayload = (selectedAddress: IAddress | undefined): UpdateMissionPayload => {
    const payload: UpdateMissionPayload = {};
    const snap = initialSnapshot.value;
    if (!snap) return payload;

    if (orderName.value !== snap.name) {
        payload.name = orderName.value || undefined;
    }
    if (hiringReason.value?.value !== snap.hiringReasonId) {
        payload.hiringReason = hiringReason.value?.value ?? null;
    }
    if (justification.value !== snap.justification) {
        payload.justification = justification.value || null;
    }

    const currentAddressJson = JSON.stringify(selectedAddress);
    if (currentAddressJson !== snap.addressJson && selectedAddress) {
        payload.address = mapAddressToOrderAddress(selectedAddress);
    }

    const currentContractType = String(contract.value.value).toUpperCase();
    if (currentContractType !== snap.contractTypeValue) {
        payload.contractType = currentContractType;
    }
    if (quantity.value !== snap.totalPositions) {
        payload.totalPositions = quantity.value;
    }
    if (showSalaryInProposal.value !== snap.showRemuneration) {
        payload.showRemuneration = showSalaryInProposal.value;
    }

    const currentRemunerationJson = JSON.stringify(propositionRemuneration.value);
    if (currentRemunerationJson !== snap.remunerationJson) {
        payload.remunerationPeriodicity = hasRemuneration.value ? propositionRemuneration.value.periodicity : null;
        payload.minRemuneration = hasRemuneration.value ? propositionRemuneration.value.minimumAmount : null;
        payload.maxRemuneration = hasRemuneration.value ? propositionRemuneration.value.maximumAmount : null;
    }

    const currentPeriodsJson = JSON.stringify(dateRanges.value);
    if (currentPeriodsJson !== snap.periodsJson && dateRanges.value.length > 0) {
        payload.periods = dateRanges.value.map(mapDateRangeToPeriod);
    }
    if (description.value !== snap.description) {
        payload.description = description.value || null;
    }

    const currentNote = showInternalNote.value ? note.value : "";
    if (currentNote !== snap.note) {
        payload.note = currentNote || null;
    }

    return payload;
};

const buildEditPatch = (payload: UpdateMissionPayload, selectedAddress: IAddress | undefined): MissionRowPatch => ({
    name: payload.name,
    totalPositions: payload.totalPositions,
    contractType: payload.contractType,
    city: payload.address ? selectedAddress?.city : undefined,
    periods: payload.periods ? buildOptimisticPeriods() : undefined,
});

const handleDateRangesUpdate = (ranges: DateRange[]) => {
    dateRanges.value = ranges;
};

const handleClose = () => {
    props.onCancel?.();
    emit("close");
};

const handleAddressChange = (selectedAddress: IAddress) => {
    address.value = selectedAddress;
    error.value.address = false;
    isLocationsModalOpen.value = false;
};

const handleOpenLocationsModal = () => {
    isLocationsModalOpen.value = true;
};

const handleCloseLocationsModal = () => {
    isLocationsModalOpen.value = false;
};

const handleOpenJustificationModal = () => {
    modalStore.actions.open(ModalName.JustificationModal, {
        defaultText: justification.value,
        onValidate: (value: string) => {
            justification.value = value;
        },
    });
};

const handleQuantityChange = (value: string | number) => {
    quantity.value = Number.parseInt(String(value), 10);
    error.value.quantity = false;
};

const handleClearRemuneration = () => {
    propositionRemuneration.value = { ...DEFAULT_REMUNERATION };
};

const handleSubmit = async () => {
    isLoading.value = true;
    const selectedAddress = address.value;

    const requiredFields: Array<{ key: string; valid: boolean; message: string }> = [
        {
            key: "address",
            valid: Boolean(selectedAddress),
            message: t("page.order.error.required", { field: t("page.order.address").toLowerCase() }),
        },
        {
            key: "contract",
            valid: Boolean(contract.value.value),
            message: t("page.order.error.required", { field: t("page.order.contract").toLowerCase() }),
        },
        {
            key: "quantity",
            valid: Number.isFinite(quantity.value) && quantity.value > 0,
            message: t("page.order.error.minQuantity"),
        },
        {
            key: "dates",
            valid: hasDates.value,
            message: t("page.order.error.required", { field: t("page.order.dates").toLowerCase() }),
        },
    ];

    for (const field of requiredFields) {
        error.value[field.key] = !field.valid;
        if (!field.valid) {
            toastError(field.message);
        }
    }

    const isValid = !Object.values(error.value).some(Boolean) && validate(true);
    if (!isValid) {
        isLoading.value = false;
        return;
    }

    if (isEdit.value && props.mission?.id) {
        const updatePayload = buildUpdatePayload(selectedAddress);
        let rollback: (() => void) | undefined = missionPatchStore.applyPatch(
            props.mission.id,
            buildEditPatch(updatePayload, selectedAddress),
        );
        try {
            await missionService.updateMission(props.mission.id, updatePayload);
            rollback = undefined;
            props.mission.onUpdated?.(updatePayload);
            toastSuccess(t("page.order.editSuccess"));
            await props.mission.onSuccess?.();
            emit("success");
            emit("close");
        } catch (submitError) {
            rollback?.();
            console.error("Error while submitting order:", submitError);
            toastError(getSubmitErrorMessage(submitError));
        } finally {
            isLoading.value = false;
        }
        return;
    }

    // The detail store only holds the missions of the company need currently displayed
    const isDisplayedCompanyNeed = companyNeedStore.companyNeedId === props.besoinId;
    const optimisticMission = buildOptimisticMission(selectedAddress, isDisplayedCompanyNeed);
    if (isDisplayedCompanyNeed) companyNeedStore.addOptimisticMission(optimisticMission);

    try {
        let createdOrder: Awaited<ReturnType<typeof missionService.createMission>> | undefined;
        try {
            createdOrder = await missionService.createMission(buildCreatePayload(selectedAddress));
        } catch (submitError) {
            companyNeedStore.removeOptimisticMission(optimisticMission.id);
            console.error("Error while submitting order:", submitError);
            toastError(getSubmitErrorMessage(submitError));
            return;
        }

        if (!createdOrder?.id) {
            companyNeedStore.removeOptimisticMission(optimisticMission.id);
            toastError(t("page.order.error.createErrorNoId"));
            return;
        }

        const createdMission: MissionList = {
            ...optimisticMission,
            id: createdOrder.id,
            status: toKnownMissionStatus(createdOrder.status, optimisticMission.status),
        };
        companyNeedStore.reconcileOptimisticMission(optimisticMission.id, {
            id: createdMission.id,
            status: createdMission.status,
        });
        if (isDisplayedCompanyNeed) void companyNeedStore.fillCreatedMissionActions(createdMission.id);

        toastSuccess(t("page.order.createSuccess"));

        try {
            await props.onSuccess?.(createdOrder);
            if (isDuplicate.value) await props.mission?.onSuccess?.(createdMission);
        } catch (callbackError) {
            console.error("Error while running onSuccess callback:", callbackError);
        }

        emit("success");
        emit("close");
    } finally {
        isLoading.value = false;
    }
};

onMounted(async () => {
    if (!isEdit.value && !props.mission?.name) void loadCompanyNeedMissionIds();

    if (props.companyId) {
        try {
            companyAddresses.value = await clientService.fetchCompanyAddresses(props.companyId);
        } catch {
            // Fallback to besoinAddress via missionLocations computed.
        }
    }

    const mission = props.mission;
    if (mission) {
        if (mission.name) orderName.value = mission.name;
        if (mission.address) address.value = mission.address;
        if (mission.totalPositions) quantity.value = mission.totalPositions;
        if (mission.description) description.value = mission.description;
        if (mission.note) {
            note.value = mission.note;
            showInternalNote.value = true;
        }
        if (mission.justification) justification.value = mission.justification;
        if (mission.contractType) {
            contract.value = {
                label: t(`mission.job.contractType.select.${mission.contractType}`),
                value: mission.contractType,
            };
        }
        if (mission.hiringReason) {
            const match = configStore.hiringReasonOptions.find((option) => option.value === mission.hiringReason);
            if (match) hiringReason.value = match;
        }
        if (mission.periods && mission.periods.length > 0) {
            dateRanges.value = mission.periods;
        }
        if (mission.showRemuneration !== undefined) {
            showSalaryInProposal.value = mission.showRemuneration;
        }
        if (isEdit.value) {
            initialSnapshot.value = takeSnapshot();
        }
        return;
    }

    if (props.besoinAddress) address.value = props.besoinAddress;
    if (props.positionsNumber) quantity.value = props.positionsNumber;
    if (props.contractType) {
        contract.value = {
            label: t(`mission.job.contractType.select.${props.contractType}`),
            value: props.contractType,
        };
    }
});
</script>

<template>
    <div class="manage-order-modal">
        <Modal position="top" :title="modalTitle" :open="open" @close="handleClose" width="720px" close disableOverlayClose center-header>
            <div class="manage-order-modal__form">
                <TextInputAlt size="md" variant="transparent" v-model="orderName" :placeholder="t('page.order.orderName')" />

                <div class="manage-order-modal__section">
                    <div class="manage-order-modal__item-title typo-xxs-medium">
                        <i class="ti ti-selector typo-sm-regular"></i>
                        <span>{{ t("page.order.reason") }}</span>
                    </div>
                    <HiringReasonsDropdown
                        variant="ghost"
                        :value="hiringReason"
                        :placeholder="t('page.order.reasonPlaceholder')"
                        icon-left=""
                        full-width
                        @input="(value) => (hiringReason = value)"
                    />

                    <div class="manage-order-modal__item-title typo-xxs-medium">
                        <i class="ti ti-answer-text typo-sm-regular"></i>
                        <span>{{ t("page.order.justification") }}</span>
                    </div>
                    <ComboButton
                        :label="justification || t('page.order.addJustification')"
                        variant="ghost"
                        @click="handleOpenJustificationModal"
                        :placeholder="!justification"
                    />

                    <div class="manage-order-modal__item-title typo-xxs-medium">
                        <i class="ti ti-map-pin-circle typo-sm-regular"></i>
                        <div class="manage-order-modal__item-title-required">
                            <span>{{ t("page.order.address") }}</span>
                            <span class="manage-order-modal__required">*</span>
                        </div>
                    </div>
                    <ComboButton
                        class="manage-order-modal__address"
                        :label="address ? formattedAddress(address) : t('page.order.addPlace')"
                        variant="ghost"
                        :error="error.address"
                        @click="handleOpenLocationsModal"
                        :placeholder="!address"
                    />

                    <div class="manage-order-modal__item-title typo-xxs-medium">
                        <i class="ti ti-signature typo-sm-regular"></i>
                        <div class="manage-order-modal__item-title-required">
                            <span>{{ t("page.order.contract") }}</span>
                            <span class="manage-order-modal__required">*</span>
                        </div>
                    </div>
                    <ContractsDropdown
                        v-model="contract"
                        :error="error.contract"
                        @update:model-value="error.contract = false"
                        :show-icon="false"
                        full-width
                    />

                    <div class="manage-order-modal__item-title typo-xxs-medium">
                        <i class="ti ti-users typo-sm-regular"></i>
                        <div class="manage-order-modal__item-title-required">
                            <span>{{ t("page.order.quantity") }}</span>
                            <span class="manage-order-modal__required">*</span>
                        </div>
                    </div>
                    <TextInputAlt
                        variant="grey2"
                        type="number"
                        state="filled"
                        :model-value="quantity.toString()"
                        :placeholder="t('page.order.quantity')"
                        :error="error.quantity"
                        :min="1"
                        @update:model-value="handleQuantityChange"
                    />

                    <div
                        class="manage-order-modal__item-title manage-order-modal__item-title--switch typo-xxs-medium"
                        @click="showSalaryInProposal = !showSalaryInProposal"
                    >
                        <Toggle size="sm" :active="showSalaryInProposal" />
                        <span>{{ t("page.order.showRemunerationInProposal") }}</span>
                    </div>

                    <template v-if="showSalaryInProposal">
                        <div class="manage-order-modal__item-title typo-xxs-medium">{{ t("page.order.proposition") }}</div>
                        <div class="manage-order-modal__remuneration">
                            <RemunerationDropdown v-model="propositionRemuneration" icon="" :input-mode="InputMode.TEXT" />
                            <ButtonAlt
                                v-if="hasRemuneration"
                                icon-only="trash"
                                type="neutral"
                                variant="ghost"
                                size="sm"
                                @click="handleClearRemuneration"
                            />
                        </div>
                    </template>
                </div>

                <ModalSection :title="t('page.order.dates')" required />
                <DateRangeTable :date-ranges="dateRanges" :min-date="effectiveMinDate" @ranges="handleDateRangesUpdate" />

                <ModalSection :title="t('page.order.description')" />
                <div class="manage-order-modal__description">
                    <TextareaInput :placeholder="t('page.order.descriptionPlaceholder')" v-model="description" auto-resize no-border />
                    <ButtonAlt
                        v-if="!showInternalNote"
                        type="neutral"
                        variant="stroke"
                        size="sm"
                        :label="t('page.order.addNote.label')"
                        @click="showInternalNote = true"
                    />
                    <TextareaInput v-else :placeholder="t('page.order.addNote.placeholder')" v-model="note" auto-resize no-border />
                </div>
            </div>
            <template #footer>
                <div class="manage-order-modal__button-container manage-order-modal__button-container--flex-end">
                    <ButtonAlt
                        type="neutral"
                        variant="ghost"
                        :label="t('global.actions.cancel')"
                        @click="handleClose"
                    />
                    <ButtonAlt
                        type="primary"
                        variant="filled"
                        :label="isEdit ? t('global.actions.save') : t('page.order.createOrder')"
                        @click="handleSubmit"
                        :loading="isLoading"
                        :disabled="isLoading || !isFormValid"
                    />
                </div>
            </template>
        </Modal>

        <MissionLocationModal
            :is-modal-open="isLocationsModalOpen"
            :mission-locations="missionLocations"
            @close="handleCloseLocationsModal"
            @address="handleAddressChange"
        />
    </div>
</template>

<style scoped lang="scss">
.manage-order-modal {
    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
    }

    &__section {
        display: grid;
        grid-template-columns: 124px 240px;
        align-items: center;
        gap: var(--spacing-md, 8px);
        container-type: inline-size;

        .manage-order-modal__address {
            width: max-content;
            max-width: calc(100cqi - 124px - var(--spacing-md, 8px));
        }
    }

    &__item-title {
        display: flex;
        white-space: nowrap;
        gap: var(--spacing-sm, 6px);
        color: var(--text-sub-600, #494e61);

        .ti {
            color: var(--icon-sub-500, #636778);
        }

        &--switch {
            display: flex;
            align-items: center;
            grid-column: span 2;
            gap: var(--spacing-md, 8px);
            cursor: pointer;
            width: fit-content;
            user-select: none;
        }
    }

    &__item-title-required {
        display: flex;
        gap: var(--spacing-xs, 4px);
    }

    &__required {
        color: var(--blue-base, #2051f8);
    }

    &__remuneration {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs, 4px);
    }

    &__description {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-lg, 12px);
    }

    &__button-container {
        display: flex;
        gap: var(--spacing-md, 8px);

        &--flex-end {
            justify-content: flex-end;
        }
    }
}
</style>
